import { useCallback, useRef, useState } from 'react'

/**
 * Downloading an export used to be `window.open`: a tab flashed, nothing moved, and a year of
 * reservations takes several seconds to build on the server before a single byte arrives. People
 * clicked again, or gave up thinking it had failed.
 *
 * Fetching it ourselves lets the page say what is happening. There are two distinct waits: the
 * server building the sheet (silent — no bytes to count, so no percentage exists) and the file
 * coming down (countable, the response carries Content-Length).
 */
type Phase = 'idle' | 'preparing' | 'downloading'

const FILENAME = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i

function nameFromHeaders(res: Response, fallback: string): string {
  const m = FILENAME.exec(res.headers.get('Content-Disposition') ?? '')
  return m ? decodeURIComponent(m[1]) : fallback
}

function mb(bytes: number): string {
  return `${(bytes / 1048576).toLocaleString('it-IT', { maximumFractionDigits: 1 })} MB`
}

export function useExportDownload() {
  const [phase, setPhase] = useState<Phase>('idle')
  const [label, setLabel] = useState('')
  const [error, setError] = useState<string | null>(null)
  // A second click while one export is in flight would start the whole server-side build again.
  const running = useRef(false)

  const run = useCallback(async (url: string, fallbackName = 'export.xlsx') => {
    if (running.current) return
    running.current = true
    setError(null)
    setPhase('preparing')
    setLabel('Preparazione del file…')

    try {
      // Resolves when the headers arrive — i.e. the moment the server finishes building the sheet.
      const res = await fetch(url, { credentials: 'include' })
      if (!res.ok) throw new Error(`Il server ha risposto ${res.status}`)

      // An expired session answers 200 with the login page, which would otherwise be saved as a
      // .xlsx full of HTML.
      const type = res.headers.get('Content-Type') ?? ''
      if (type.includes('text/html')) throw new Error('Sessione scaduta: rientra nel gestionale e riprova.')

      const total = Number(res.headers.get('Content-Length')) || 0
      setPhase('downloading')
      setLabel(total ? 'Scaricamento 0%' : 'Scaricamento…')

      let blob: Blob
      if (res.body) {
        const reader = res.body.getReader()
        const chunks: BlobPart[] = []
        let received = 0
        for (;;) {
          const { done, value } = await reader.read()
          if (done) break
          chunks.push(value)
          received += value.length
          setLabel(total ? `Scaricamento ${Math.round((received / total) * 100)}%` : `Scaricamento ${mb(received)}`)
        }
        blob = new Blob(chunks, { type })
      } else {
        blob = await res.blob()
      }

      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = href
      a.download = nameFromHeaders(res, fallbackName)
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(href)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Esportazione non riuscita. Riprova.')
    } finally {
      running.current = false
      setPhase('idle')
      setLabel('')
    }
  }, [])

  return { run, busy: phase !== 'idle', label, error, clearError: () => setError(null) }
}
